import enrolLoyaltyAccount from 'actions/loyalty/enrolLoyaltyAccount'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Typography from 'LuxKit/Typography/Typography'
import Group from 'components/utils/Group'
import HeroImage from 'content/components/HeroImage'
import ModalContext from 'contexts/ModalContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getThemeClassName } from 'lib/theme/themeUtils'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import { selectLoggedIn } from 'selectors/accountSelectors'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LoyaltyLandingPageUSPSection from '../loyaltyLandingPageSharedSections/LoyaltyLandingPageUSPSection'
import LoyaltyLandingPageHowItWorksSection from '../loyaltyLandingPageSharedSections/LoyaltyLandingPageHowItWorksSection'
import LoyaltyLandingPageLargeTile from '../loyaltyLandingPageSharedSections/LoyaltyLandingPageLargeTile'
import LoyaltyLandingPageSmallTile from '../loyaltyLandingPageSharedSections/LoyaltyLandingPageSmallTile'
import LoyaltyLandingPageBenefitsTableSection from '../loyaltyLandingPageSharedSections/LoyaltyLandingPageBenefitsTableSection'
import LoyaltyLandingPageDiscoverTiersSection from '../loyaltyLandingPageSharedSections/LoyaltyLandingPageDiscoverTiersSection'
import LoyaltyLandingPageFaqSection from '../loyaltyLandingPageSharedSections/LoyaltyLandingPageFaqSection'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TriplePointTakeoverHeroCarousel from 'takeovers/societyTriplePointsTakeover/components/TriplePointTakeoverHeroCarousel'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { triplePointsTakeoverId } from 'takeovers/societyTriplePointsTakeover/societyTriplePointsTakeoverConfig'
import useLoyaltyLandingPageBenefitCopy from 'loyalty/hooks/useLoyaltyLandingPageBenefitCopy'
import { EmptyArray } from 'lib/array/arrayUtils'
import LoyaltyAccountCurrentOffersCarousel from '../loyaltyUserAccountPage/LoyaltyAccountCurrentOffersCarousel'

const HeroSection = styled(HeroImage)`
  padding: ${rem(60)} 0;
  height: auto;
  min-height: auto;
`

const ContentSection = styled(Pane)`
  padding: ${rem(24)} 0;

  ${mediaQueryUp.tablet} {
    padding: ${rem(68)} 0;
  }
`

const SmallTilesSection = styled.div`
  display: grid;
  gap: ${rem(24)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(3, 1fr);
  }
`

function LoyaltyGuestLandingPage() {
  const showModal = useContext(ModalContext)
  const dispatch = useAppDispatch()
  const onSignInClick = useCallback(() => showModal(<AccountAccessModal initialMode="loyaltyLogin" />), [showModal])
  const isLoggedIn = useAppSelector(state => selectLoggedIn(state))
  const triplePointCampaignActive = useCurrentSiteTakeover()?.id === triplePointsTakeoverId
  const [loyaltyLandingPageBenefitTiles] = useLoyaltyLandingPageBenefitCopy()

  const largeTiles = useMemo(() => {
    if (!loyaltyLandingPageBenefitTiles) return EmptyArray
    return [
      loyaltyLandingPageBenefitTiles.hotel_upgrade,
      loyaltyLandingPageBenefitTiles.airport_transfer,
      loyaltyLandingPageBenefitTiles.lounge_pass,
    ].filter(tile => tile?.enabled)
  }, [loyaltyLandingPageBenefitTiles])

  const smallTiles = useMemo(() => {
    if (!loyaltyLandingPageBenefitTiles) return EmptyArray
    return [
      loyaltyLandingPageBenefitTiles.phone_support,
      loyaltyLandingPageBenefitTiles.travelshoot_credit,
      loyaltyLandingPageBenefitTiles.tour_exp_credit,
    ].filter(tile => tile?.enabled)
  }, [loyaltyLandingPageBenefitTiles])

  const onJoinClick = useCallback(async() => {
    let loginResult: AccountAccessModalResult = { loggedIn: isLoggedIn }

    if (!isLoggedIn) {
      loginResult = await showModal<AccountAccessModalResult>(<AccountAccessModal initialMode="loyaltyLogin" />)
    }
    if (loginResult.loggedIn) {
      dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
    }
  }, [dispatch, isLoggedIn, showModal])

  return <div>
    <HeroSection
      id="0uklktwbgmt5rpgo6yo8"
      dpr={2}
      brightness={-40}
      className={getThemeClassName({ mobile: 'inverse' })}
    >
      <LayoutContainer variant="medium">
        <VerticalSpacer gap={16} style={{ position: 'relative' }}>
          <LoyaltyProgramNameLogo width={104} />
          <Group
            direction="vertical"
            tabletDirection="horizontal"
            tabletHorizontalAlign="space-between"
            gap={24}
            tabletGap={16}
          >
            <Heading variant="heading1">
              The ultimate rewards program<CSSBreakpoint min="tablet" as="br" />{' '}
              to elevate every escape
            </Heading>
            <Group
              direction="vertical"
              horizontalAlign="start"
              tabletHorizontalAlign="end"
              tabletVerticalAlign="end"
              gap={16}
            >
              <TextButton kind="primary" onClick={onJoinClick}>
                Join now - It’s free
              </TextButton>
              <Group direction="horizontal" gap={12} wrap="wrap" tabletWrap="nowrap">
                <BodyText variant="medium" wrap="no-wrap">Already have an account?</BodyText>
                <TextLink size="medium" onClick={onSignInClick} wrap="no-wrap">Sign in</TextLink>
              </Group>
            </Group>
          </Group>
        </VerticalSpacer>
      </LayoutContainer>
    </HeroSection>
    {triplePointCampaignActive && <TriplePointTakeoverHeroCarousel
      viewOffersTo="/triple-points"
    />}
    <LoyaltyLandingPageUSPSection />
    <CSSBreakpoint max="mobile">
      <LoyaltyAccountCurrentOffersCarousel />
    </CSSBreakpoint>
    <ContentSection background="neutral-seven">
      <LayoutContainer variant="medium">
        <VerticalSpacer gap={32}>
          <VerticalSpacer gap={8}>
            <Heading variant="heading2" align="center">Where loyalty meets luxury</Heading>
            <BodyText variant="large" align="center">
              Every booking brings you closer to unlocking VIP benefits,{' '}
              adding more luxury to every escape.
            </BodyText>
          </VerticalSpacer>
          <VerticalSpacer gap={48}>
            <VerticalSpacer gap={36}>
              {largeTiles.map((tile, index) => <LoyaltyLandingPageLargeTile
                reverse={index % 2 === 0}
                tile={tile}
                key={index}
              />)}
            </VerticalSpacer>
            <SmallTilesSection>
              {smallTiles.map(tile => <LoyaltyLandingPageSmallTile key={tile.image.id} tile={tile} />)}
            </SmallTilesSection>
            <Typography align="center">
              <TextButton kind="primary" onClick={onJoinClick}>
                Join now - it's free
              </TextButton>
            </Typography>
          </VerticalSpacer>
        </VerticalSpacer>
      </LayoutContainer>
    </ContentSection>
    <LoyaltyLandingPageDiscoverTiersSection />
    <LoyaltyLandingPageBenefitsTableSection onJoin={onJoinClick} />
    <LoyaltyLandingPageHowItWorksSection onJoin={onJoinClick} />
    <CSSBreakpoint min="tablet">
      <LoyaltyAccountCurrentOffersCarousel />
    </CSSBreakpoint>
    <LoyaltyLandingPageFaqSection slug="societe-faqs" />

  </div>
}

export default LoyaltyGuestLandingPage
